import { useCallback, useEffect, useRef, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Box,
  Button,
  HStack,
  Select,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { Check, UserX, Users } from 'lucide-react';
import { WS_EVENTS } from '@twinden/shared';
import { useWebSocket } from '../../hooks/useWebSocket';
import {
  api,
  ApiError,
  type FamilyRole,
  type JoinRequest,
} from '../../lib/api';
import { formatDate } from '../../lib/format';
import { ConfirmSheet } from '../ConfirmSheet';
import { useT, type MessageKey } from '../../i18n';

interface JoinRequestsPanelProps {
  familyId: string;
  /** Only roots grant root (D1, D154). */
  canGrantRoot: boolean;
  /** Called with the number still waiting, for badges. */
  onCountChange?: (count: number) => void;
}

const ROLE_LABEL: Record<FamilyRole, MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

type DecidedPayload = { familyId: string; requestId: string };
type CreatedPayload = { familyId: string };

// People asking to join with the family link (D154): roots and admins
// approve (with a role) or decline; the first decision wins and everyone
// else's list follows over the family WS events.
export function JoinRequestsPanel({
  familyId,
  canGrantRoot,
  onCountChange,
}: JoinRequestsPanelProps) {
  const t = useT();
  const toast = useToast();
  const { onEvent } = useWebSocket();
  const [requests, setRequests] = useState<JoinRequest[] | null>(null);
  const [roles, setRoles] = useState<Record<string, FamilyRole>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [declining, setDeclining] = useState<JoinRequest | null>(null);

  // useT and useToast hand out new functions on every render; keep them out
  // of load's identity so the list isn't fetched again after each change.
  const say = useRef({ t, toast });
  say.current = { t, toast };

  // The small mark while the requests load (M66, D250).
  const requestsState = useLoadingState({ loading: requests === null });

  const load = useCallback(async () => {
    try {
      const response = await api.getJoinRequests(familyId);
      setRequests(response.data.requests);
    } catch (err) {
      setRequests((current) => current ?? []);
      const { t: tr, toast: show } = say.current;
      show({
        title: tr('account.requests.loadFailed'),
        description:
          err instanceof ApiError ? err.message : tr('common.somethingWrong'),
        status: 'error',
      });
    }
  }, [familyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event === WS_EVENTS.JOIN_REQUEST_DECIDED) {
          const decided = payload as DecidedPayload;
          if (decided.familyId !== familyId) return;
          setRequests(
            (current) =>
              current?.filter((r) => r.id !== decided.requestId) ?? null
          );
        } else if (event === WS_EVENTS.JOIN_REQUEST_CREATED) {
          if ((payload as CreatedPayload).familyId === familyId) void load();
        }
      }),
    [onEvent, familyId, load]
  );

  const count = requests?.length ?? 0;
  useEffect(() => {
    if (requests) onCountChange?.(count);
  }, [count, requests, onCountChange]);

  const drop = (id: string) =>
    setRequests((current) => current?.filter((r) => r.id !== id) ?? null);

  const fail = (err: unknown) =>
    toast({
      title: t('account.requests.failed'),
      description:
        err instanceof ApiError ? err.message : t('common.somethingWrong'),
      status: 'error',
    });

  const approve = async (request: JoinRequest) => {
    setBusy(request.id);
    try {
      await api.approveJoinRequest(
        familyId,
        request.id,
        roles[request.id] ?? 'member'
      );
      drop(request.id);
      toast({
        title: t('account.requests.approved', {
          name: request.user.displayName,
        }),
        status: 'success',
      });
    } catch (err) {
      fail(err);
      // Someone else may have decided first; show the list as it is now.
      void load();
    } finally {
      setBusy(null);
    }
  };

  const decline = async () => {
    const request = declining;
    if (!request) return;
    setBusy(request.id);
    try {
      await api.declineJoinRequest(familyId, request.id);
      drop(request.id);
      toast({ title: t('account.requests.declined'), status: 'success' });
    } catch (err) {
      fail(err);
      void load();
    } finally {
      setBusy(null);
      setDeclining(null);
    }
  };

  const approveAll = async () => {
    setBusy('all');
    try {
      const response = await api.approveAllJoinRequests(familyId);
      toast({
        title: t('account.requests.approvedAll', {
          count: response.data.approved,
        }),
        status: 'success',
      });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
      void load();
    }
  };

  if (requests === null || requestsState.show) {
    return <LoadingPanel state={requestsState} size="inline" />;
  }

  if (requests.length === 0) {
    return (
      <VStack py={8} spacing={2} color="muted" textAlign="center">
        <Users size={28} aria-hidden="true" />
        <Text fontSize="15px">{t('account.requests.empty')}</Text>
      </VStack>
    );
  }

  // Only roots appoint admins and roots (identity spec 6.1, D154); an admin
  // approves as member only, so there is nothing to choose.
  const roleChoices: FamilyRole[] = canGrantRoot
    ? ['member', 'admin', 'root']
    : ['member'];

  return (
    <VStack align="stretch" spacing={3}>
      {requests.length > 1 && (
        <Button
          alignSelf="flex-start"
          leftIcon={<Check size={16} />}
          isLoading={busy === 'all'}
          isDisabled={busy !== null && busy !== 'all'}
          onClick={() => void approveAll()}
        >
          {t('account.requests.approveAll', { count: requests.length })}
        </Button>
      )}
      {requests.map((request) => (
        <Box
          key={request.id}
          bg="surface"
          p={4}
          borderRadius="card"
          borderWidth="1px"
          borderColor="line"
        >
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={3}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
          >
            <HStack spacing={3} align="start" minW={0}>
              <Avatar
                name={request.user.displayName}
                src={request.user.avatarUrl ?? undefined}
                size="md"
              />
              <VStack align="start" spacing={0} minW={0}>
                <Text fontWeight={700} fontSize="17px" noOfLines={1}>
                  {request.user.displayName}
                </Text>
                {/* Two lines on phones, so the date isn't cut off. */}
                <Text fontSize="14px" color="muted" noOfLines={2}>
                  @{request.user.handle}
                  {request.sharedBy
                    ? ` · ${t('account.requests.via', { name: request.sharedBy.displayName })}`
                    : ''}
                  {' · '}
                  {formatDate(new Date(request.createdAt))}
                </Text>
                {request.note && (
                  <Text fontSize="15px" color="ink.body" mt={1}>
                    “{request.note}”
                  </Text>
                )}
              </VStack>
            </HStack>
            <HStack spacing={2} flexShrink={0}>
              {roleChoices.length > 1 && (
                <Select
                  size="sm"
                  w="auto"
                  borderRadius="full"
                  aria-label={t('account.requests.roleFor', {
                    name: request.user.displayName,
                  })}
                  value={roles[request.id] ?? 'member'}
                  onChange={(event) =>
                    setRoles((current) => ({
                      ...current,
                      [request.id]: event.target.value as FamilyRole,
                    }))
                  }
                >
                  {roleChoices.map((role) => (
                    <option key={role} value={role}>
                      {t(ROLE_LABEL[role])}
                    </option>
                  ))}
                </Select>
              )}
              <Button
                size="sm"
                isLoading={busy === request.id}
                isDisabled={busy !== null && busy !== request.id}
                onClick={() => void approve(request)}
              >
                {t('account.requests.approve')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                isDisabled={busy !== null}
                onClick={() => setDeclining(request)}
              >
                {t('account.requests.decline')}
              </Button>
            </HStack>
          </Stack>
        </Box>
      ))}

      <ConfirmSheet
        isOpen={declining !== null}
        onClose={() => setDeclining(null)}
        icon={<UserX size={28} />}
        title={t('account.requests.declineTitle', {
          name: declining?.user.displayName ?? '',
        })}
        body={t('account.requests.declineBody')}
        confirmLabel={t('account.requests.decline')}
        onConfirm={() => void decline()}
        isLoading={busy !== null && busy === declining?.id}
      />
    </VStack>
  );
}
